// GFM refuses to parse a table when the delimiter row's cell count doesn't
// match the header row's, so `| A | B | C | D |` over `|---|---|---|` renders
// as a plain paragraph with the newlines collapsed — a frequent LLM output
// defect. Repair the delimiter row to match the header before parsing.
// Everything else (missing blank lines, short body rows, bare delimiters)
// marked already tolerates, so this is the only repair we do.

// Same fence pattern the streaming block splitter uses (markdown-stream.ts).
const FENCE_OPEN = /^[ \t]{0,3}(`{3,}|~{3,})/

// Indented code blocks (4+ spaces or a tab) must not be rewritten.
const CODE_INDENT = /^(?: {4,}|\t)/

// Mask escaped backslashes first so backslash parity matches GFM: in
// `C:\Users\\ |` the pipe is a real separator (the backslash before it is
// itself escaped), while in `a \| b` the pipe is cell text.
function maskEscapedPipes(line: string) {
  return line.replace(/\\\\/g, "\0\0").replace(/\\\|/g, "\0\0")
}

// Split a table row into cells following GFM: strip at most one leading and
// one trailing pipe, then split on unescaped pipes.
function splitCells(line: string): string[] {
  let s = maskEscapedPipes(line.trim())
  if (s.startsWith("|")) s = s.slice(1)
  if (s.endsWith("|")) s = s.slice(0, -1)
  return s.split("|")
}

// A line the author meant as a table delimiter: only pipes, colons, dashes
// and whitespace, with at least one dash and at least one pipe (the pipe
// requirement keeps setext headings like `Text\n---` out), and every cell a
// valid alignment spec.
function isDelimiterRow(line: string): boolean {
  const t = line.trim()
  if (!t.includes("|") || !t.includes("-")) return false
  if (!/^[|:\-\s]+$/.test(t)) return false
  const cells = splitCells(line)
  if (cells.length === 0) return false
  return cells.every((cell) => /^:?-+:?$/.test(cell.trim()))
}

// Rebuild the delimiter with exactly `count` cells, keeping the alignment
// specs the author already wrote and padding the rest with `---`.
function rebuildDelimiter(delimiterCells: string[], count: number, indent: string): string {
  const specs = delimiterCells.map((cell) => cell.trim())
  const next: string[] = []
  for (let i = 0; i < count; i++) next.push(specs[i] ?? "---")
  return `${indent}| ${next.join(" | ")} |`
}

export function normalizeMarkdownTables(src: string): string {
  if (!src.includes("|")) return src

  // marked normalizes CRLF itself, but this line pass must too: a "\r" left
  // on a line keeps the closing-fence regex from matching, locking the
  // scanner inside the fence for the rest of the document.
  const lines = src.replace(/\r\n?/g, "\n").split("\n")
  let fence: { char: string; size: number } | null = null

  for (let i = 0; i + 1 < lines.length; i++) {
    const line = lines[i]

    if (fence) {
      const close = new RegExp(`^[ \\t]{0,3}${"\\" + fence.char}{${fence.size},}[ \\t]*$`)
      if (close.test(line)) fence = null
      continue
    }

    const opened = line.match(FENCE_OPEN)
    if (opened) {
      const mark = opened[1]
      fence = { char: mark[0], size: mark.length }
      continue
    }

    if (CODE_INDENT.test(line)) continue
    if (!line.includes("|")) continue
    if (isDelimiterRow(line)) continue

    const next = lines[i + 1]
    if (!isDelimiterRow(next)) continue

    const headerCount = splitCells(line).length
    if (headerCount < 2) continue

    const delimiterCells = splitCells(next)
    if (delimiterCells.length === headerCount) {
      i++
      continue
    }

    const indent = next.match(/^[ \t]*/)?.[0] ?? ""
    lines[i + 1] = rebuildDelimiter(delimiterCells, headerCount, indent)
    i++
  }

  return lines.join("\n")
}
